import React, { useState, useEffect } from 'react';
import { IoWarningOutline } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';

const Users = () => {
  const navigate = useNavigate();

  // State management
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  // State for confirmation dialog
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [dialogMessage, setDialogMessage] = useState('');

  // State for success/error notification
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationType, setNotificationType] = useState('');
  const [notificationTimer, setNotificationTimer] = useState(null);

  // Get userLabId and token from localStorage
  const userLabId = localStorage.getItem('userLabId');
  const token = localStorage.getItem('accessToken') || localStorage.getItem('token');

  // Helper to get auth headers
  const getAuthHeaders = () => {
    if (!token) {
      throw new Error('No authentication token found. Please log in again.');
    }
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  // Show notification function
  const showNotificationMessage = (message, type) => {
    if (notificationTimer) clearTimeout(notificationTimer);
    setNotificationMessage(message);
    setNotificationType(type);
    setShowNotification(true);
    const timer = setTimeout(() => setShowNotification(false), 3000);
    setNotificationTimer(timer);
  };

  const closeNotification = () => {
    if (notificationTimer) clearTimeout(notificationTimer);
    setShowNotification(false);
  };

  // Function to generate full domain URL from subdomain
  const generateFullDomain = (subdomain) => {
    if (!subdomain || subdomain === '') return null;
    // Remove any existing domain if present
    const cleanSubdomain = subdomain.split('.')[0];
    return `${cleanSubdomain}.hms.hiragroupcloud.com`;
  };

  // Function to open domain in new tab
  const openDomain = (subdomain) => {
    const domain = generateFullDomain(subdomain);
    if (domain) {
      window.open(`https://${domain}`, '_blank');
    }
  };

  // Fetch users on mount
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError('');

        // Check if userLabId exists
        if (!userLabId) {
          setLoading(false);
          return;
        }

        let url = `${process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com'}/api/auth/users-by-lab`;
        if (userLabId) {
          url += `?labId=${userLabId}`;
          setIsGlobalAdmin(false);
        } else {
          setIsGlobalAdmin(true);
        }

        const response = await fetch(url, {
          method: 'GET',
          headers: getAuthHeaders(),
        });

        const data = await response.json();

        if (!response.ok) {
          if (response.status === 401 || response.status === 403) {
            return;
          }
          throw new Error(data.message || 'Failed to fetch users');
        }

        if (data.success && Array.isArray(data.users)) {
          const mappedUsers = data.users.map(user => ({
            id: user.userId || user._id,
            mongoId: user._id,
            userId: user.userId,
            email: user.email || 'N/A',
            firstName: user.firstName || user.first_name || 'N/A',
            lastName: user.lastName || user.last_name || 'N/A',
            role: user.role || 'User',
            labId: user.labId || '',
            subdomain: user.subdomain || '', // Get subdomain from backend
            isCurrentUser: false,
          }));
          setUsers(mappedUsers);
          setFilteredUsers(mappedUsers);
          setCurrentPage(1);
        } else {
          setUsers([]);
          setFilteredUsers([]);
        }
      } catch (err) {
        setError(err.message || 'Something went wrong while fetching users.');
        showNotificationMessage(err.message || 'Something went wrong while fetching users.', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (token && userLabId) {
      fetchUsers();
    } else if (!token) {
      setLoading(false);
      setError('No authentication token. Please log in.');
      navigate('/login');
    } else if (!userLabId) {
      setLoading(false);
    }
  }, [userLabId, token, navigate]);

  // Client-side search filter
  useEffect(() => {
    if (!searchQuery) {
      setFilteredUsers(users);
      setCurrentPage(1);
      return;
    }
    const lowerQuery = searchQuery.toLowerCase();
    const filtered = users.filter(user =>
      user.email.toLowerCase().includes(lowerQuery) ||
      user.firstName.toLowerCase().includes(lowerQuery) ||
      user.lastName.toLowerCase().includes(lowerQuery) ||
      user.role.toLowerCase().includes(lowerQuery) ||
      (user.labId && user.labId.includes(lowerQuery)) ||
      (user.subdomain && user.subdomain.toLowerCase().includes(lowerQuery))
    );
    setFilteredUsers(filtered);
    setCurrentPage(1);
  }, [searchQuery, users]);

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredUsers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  useEffect(() => {
    if (filteredUsers.length > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [filteredUsers, currentPage, totalPages]);

  // Delete handlers
  const handleDeleteClick = (userId) => {
    const user = users.find(u => u.id === userId);
    setDialogMessage(`Are you sure you want to delete user "${user?.firstName} ${user?.lastName}"?`);
    setUserToDelete(userId);
    setShowConfirmDialog(true);
  };

  const confirmDelete = async () => {
    if (userToDelete) {
      try {
        const user = users.find(u => u.id === userToDelete);
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://hms.backend.hiragroupcloud.com'}/api/auth/delete-users/${user.userId || user.mongoId}`, {
          method: 'DELETE',
          headers: getAuthHeaders(),
        });

        if (response.ok) {
          const updatedUsers = users.filter(user => user.id !== userToDelete);
          setUsers(updatedUsers);
          setFilteredUsers(updatedUsers);
          showNotificationMessage(`User "${user.firstName} ${user.lastName}" deleted successfully!`, 'success');
        } else {
          const data = await response.json();
          throw new Error(data.message || 'Failed to delete user');
        }
      } catch (err) {
        showNotificationMessage(`Error: ${err.message}`, 'error');
      }
    }
    setShowConfirmDialog(false);
    setUserToDelete(null);
  };

  const cancelDelete = () => {
    setShowConfirmDialog(false);
    setUserToDelete(null);
  };

  const handleAddClick = () => navigate('/Addnewuser');
  const handleEditClick = (user) => {
    const editId = user.userId || user.mongoId || user.id;
    navigate(`/editUser/${editId}`);
  };

  useEffect(() => {
    return () => {
      if (notificationTimer) clearTimeout(notificationTimer);
    };
  }, [notificationTimer]);

  // Show loading state
  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center', fontSize: '16px' }}>Loading users...</div>;
  }

  // Show error state
  if (error) {
    return <div style={{ padding: '20px', color: 'red', textAlign: 'center', fontSize: '16px' }}>{error}</div>;
  }

  // Show "Please select a lab first" message if no labId found
  if (!userLabId) {
    return (
      <div style={{textAlign: 'center',padding: '50px'}}>
          <h4> Please select the lab to view  users entries for the specific lab</h4>
        </div>
            );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Confirmation Dialog Modal */}
      {showConfirmDialog && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            width: '400px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            fontFamily: 'Arial, sans-serif',
          }}>
            <div style={{
              backgroundColor: '#004aad',
              color: 'white',
              padding: '16px 20px',
              fontSize: '18px',
              fontWeight: 'bold',
            }}>
              Confirm Deletion
            </div>
            <div style={{
              padding: '30px 20px',
              textAlign: 'center',
              fontSize: '16px',
              color: '#333',
              lineHeight: '1.5',
              borderBottom: '1px solid #eee',
            }}>
              {dialogMessage}
            </div>
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '16px 20px',
              gap: '12px',
            }}>
              <button
                onClick={cancelDelete}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '8px 20px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  minWidth: '80px',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#5a6268'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#6c757d'}
              >
                No
              </button>
              <button
                onClick={confirmDelete}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '8px 20px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  minWidth: '80px',
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success/Error Notification */}
      {showNotification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: notificationType === 'success' ? '#28a745' : '#dc3545',
          color: 'white',
          padding: '15px 20px',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 1001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minWidth: '300px',
          maxWidth: '400px',
          animation: 'slideIn 0.3s ease-out',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>{notificationType === 'success' ? '✓' : '⚠️'}</span>
            <span style={{ fontSize: '14px', fontWeight: '500' }}>{notificationMessage}</span>
          </div>
          <button
            onClick={closeNotification}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '0',
              marginLeft: '15px',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            ×
          </button>
        </div>
      )}

      <h1>Users</h1>

      <div style={{
        backgroundColor: '#f4f4f4',
        padding: '20px',
        borderTop: '2px solid #004aad',
        marginBottom: '20px',
        borderRadius: '0 0 8px 8px'
      }}>
        {/* Search */}
        <div style={{ marginBottom: '15px' }}>
          <div style={{ fontSize: '14px', marginBottom: '5px', display: 'flex', alignItems: 'center' }}>
            <span style={{ marginRight: '10px', color: '#333' }}>Search:</span>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by email, name, role, lab ID, or subdomain..."
              style={{
                width: '350px',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '14px',
                outline: 'none',
                backgroundColor: 'white',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#004aad';
                e.target.style.boxShadow = '0 0 0 2px rgba(0, 74, 173, 0.3)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#ddd';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* Entries Info + Add Button */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '15px'
        }}>
          <div style={{ fontSize: '14px', color: '#555' }}>
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredUsers.length)} of {filteredUsers.length} entries
            {searchQuery && <span style={{ color: '#004aad' }}> (filtered)</span>}
          </div>
          <button
            onClick={handleAddClick}
            style={{
              backgroundColor: '#004aad',
              color: 'white',
              border: 'none',
              padding: '10px 20px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#003d8a';
              e.target.style.boxShadow = '0 4px 8px rgba(0,74,173,0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#004aad';
              e.target.style.boxShadow = 'none';
            }}
          >
            <span style={{ fontSize: '18px' }}>+</span> Add User
          </button>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse',
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <thead>
              <tr style={{
                backgroundColor: '#f8f9fa',
                borderBottom: '2px solid #dee2e6'
              }}>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Domain</th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Email</th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>First Name</th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Last Name</th>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Role</th>
                {isGlobalAdmin && (
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Lab ID</th>
                )}
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentItems.length === 0 ? (
                <tr>
                  <td colSpan={isGlobalAdmin ? 7 : 6} style={{ textAlign: 'center', padding: '40px', color: '#888', fontSize: '16px' }}>
                    No users found.
                  </td>
                </tr>
              ) : (
                currentItems.map((user, index) => {
                  const fullDomain = generateFullDomain(user.subdomain);
                  return (
                    <tr
                      key={user.id}
                      style={{
                        borderBottom: index !== currentItems.length - 1 ? '1px solid #eee' : 'none',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                    >
                      <td style={{ padding: '15px' }}>
                        {fullDomain ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <a
                              href={`https://${fullDomain}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#004aad',
                                textDecoration: 'none',
                                fontSize: '14px',
                                fontFamily: 'monospace',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                display: 'inline-block',
                                wordBreak: 'break-all'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.textDecoration = 'underline';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.textDecoration = 'none';
                              }}
                            >
                              {fullDomain}
                            </a>
                          </div>
                        ) : (
                          <span style={{ fontSize: '14px', color: '#999', fontStyle: 'italic' }}>
                            No domain
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '15px', fontSize: '14px', color: '#666' }}>{user.email}</td>
                      <td style={{ padding: '15px', fontSize: '14px', color: '#666' }}>{user.firstName}</td>
                      <td style={{ padding: '15px', fontSize: '14px', color: '#666' }}>{user.lastName}</td>
                      <td style={{ padding: '15px' }}>
                        <span style={{
                          backgroundColor: user.role === 'Admin' ? '#d4edda' : '#fff3cd',
                          color: user.role === 'Admin' ? '#155724' : '#856404',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '13px',
                          fontWeight: '500',
                          border: user.role === 'Admin' ? '1px solid #c3e6cb' : '1px solid #ffeaa7'
                        }}>
                          {user.role}
                        </span>
                      </td>
                      {isGlobalAdmin && (
                        <td style={{ padding: '15px', fontSize: '14px', color: '#666' }}>
                          {user.labId || '-'}
                        </td>
                      )}
                      <td style={{ padding: '15px' }}>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            onClick={() => handleEditClick(user)}
                            style={{
                              backgroundColor: '#004aad',
                              color: 'white',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '13px'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#003d8a'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#004aad'}
                          >
                            Edit
                          </button>
                          {!user.isCurrentUser && (
                            <button
                              onClick={() => handleDeleteClick(user.id)}
                              style={{
                                backgroundColor: '#dc3545',
                                color: 'white',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '13px'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#c82333'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#dc3545'}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {filteredUsers.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            marginTop: '20px',
            gap: '10px',
          }}>
            <button
              onClick={prevPage}
              disabled={currentPage === 1}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: currentPage === 1 ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (currentPage !== 1) e.target.style.backgroundColor = '#5a6268';
              }}
              onMouseLeave={(e) => {
                if (currentPage !== 1) e.target.style.backgroundColor = '#6c757d';
              }}
            >
              Previous
            </button>
            <span style={{ fontSize: '14px', color: '#555' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={nextPage}
              disabled={currentPage === totalPages}
              style={{
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                opacity: currentPage === totalPages ? 0.5 : 1,
              }}
              onMouseEnter={(e) => {
                if (currentPage !== totalPages) e.target.style.backgroundColor = '#5a6268';
              }}
              onMouseLeave={(e) => {
                if (currentPage !== totalPages) e.target.style.backgroundColor = '#6c757d';
              }}
            >
              Next
            </button>
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(100%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>
    </div>
  );
};

export default Users;